import React, { useCallback, useEffect, useState } from "react";
import StatisticTooltip from "./StatisticTooltip";
import { useSelector } from "react-redux";
import { selectProject } from "../../Main/main.slice";
import Loader from "components/Loader/Loader";
import { getProjectFinancials, selectState, setSelectedDates } from "../financials.slice";
import { useAppDispatch } from "hooks/hooks";
import ReceivablesAndPayables from "./ReceivablesAndPayables";
import FuelAndResources from "./FuelAndResources";
import Carnets from "./Carnets";
import Stock from "./Stock";
import { FormatDateIso, getLastDateOfMonth } from "utils/dateUtils";
import ConcreteBaseOwnProducts from "./ConcreteBaseOwnProducts";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { PROJECT_MODULE_NAME } from "Dashboard/scenes/Projects/domain/ProjectModule";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import ConcreteBaseMaterials from "./ConcreteBaseMaterials";
import DatePicker, { registerLocale } from "react-datepicker";
import srLatn from "date-fns/locale/sr-Latn";

registerLocale("sr-Latn", srLatn);

const BottomSection = () => {
  const dispatch = useAppDispatch();
  const {
    loading,
    selectedDateFrom,
    selectedDateTo,
    generalFinancials,
    accountPayableFinancials,
    accountReceivableFinancials,
    carnetFinancials,
    stockOutputFinancials,
    consumableExemptionFinancials,
    concreteBaseOwnProductsFinancials
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const [statisticActive, setStatisticActive] = useState(false);

  const openMiniStats = (event: React.MouseEvent<HTMLElement>) => {
    setStatisticActive(true);
  };

  const project = useSelector(selectProject);
  const { projectId } = project || {};

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(), null
  ]);

  const selectDate = useCallback((update: [Date | null, Date | null]) => {
    setDateRange(update);
    const [dateFrom, dateTo] = update;
    let lastDayForDateTo = dateTo != null
      ? getLastDateOfMonth(dateTo) : null;
    dispatch(setSelectedDates({
      dateFrom: dateFrom ? FormatDateIso(dateFrom) : undefined,
      dateTo: lastDayForDateTo ? FormatDateIso(lastDayForDateTo) : undefined
    }))
  }, [dispatch]);

  useEffect(() => {
    const abortCtrl = new AbortController();
    if (projectId) {
      dispatch(getProjectFinancials({
        data: {
          from: selectedDateFrom,
          to: selectedDateTo,
          projectId,
        },
        signal: abortCtrl.signal
      }));
    }
    return () => {
      if (projectId) {
        abortCtrl.abort();
      }
    };
  }, [dispatch, selectedDateFrom, selectedDateTo, projectId]);

  return (
    <section className="loading-container">
      {loading && <Loader />}
      <h1 className="h1-absolute">
        <ToolboxButton
          icon={ToolboxButtonIcon.Chart}
          tooltip="Info"
          onClick={hasWriteAccessTo(user, PROJECT_MODULE_NAME)
            ? openMiniStats : undefined}
        />
        {" "}
        {project?.projectName}
        <StatisticTooltip
          general={generalFinancials?.general}
          receivables={accountReceivableFinancials}
          payables={accountPayableFinancials}
          consumableExemption={consumableExemptionFinancials}
          carnets={carnetFinancials}
          stockOutput={stockOutputFinancials}
          concreteBaseMaterial={generalFinancials?.concreteBaseMaterialFinancials}
          concreteBaseOwnProducts={concreteBaseOwnProductsFinancials}
          project={project}
          active={statisticActive}
          setActive={setStatisticActive}
        />
      </h1>
      <div className="projects-bottom-content">
        <ReceivablesAndPayables
          projectId={projectId}
          receivables={accountReceivableFinancials}
          payables={accountPayableFinancials}
        />
        <FuelAndResources
          projectId={projectId}
          data={consumableExemptionFinancials}
        />
        <Carnets projectId={projectId} data={carnetFinancials} />
        {project?.category?.name !== 'Betonska baza'
          ? (<Stock projectId={projectId} data={stockOutputFinancials} />)
          : (<ConcreteBaseMaterials data={generalFinancials?.concreteBaseMaterialFinancials} />)}
        <ConcreteBaseOwnProducts
          projectId={projectId}
          data={concreteBaseOwnProductsFinancials}
        />
        <div className="daily-report-box">
          {<div className="monthRange">
            <h3 className="monthRangeTitle"><strong>Prikaz finansija za:</strong></h3>
            <DatePicker
              selectsRange={true}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={selectDate}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              isClearable={true}
              locale="sr-Latn"
            />
          </div>}
        </div>
      </div>
    </section>
  );
};

export default BottomSection;
